/**
 * utils/parseSenseVectorTree.js
 *
 * Builds the Spotlight category tree from a Sense `vector_map`
 * (get_combined_sense_data_of_user_by_match), shaped like:
 *
 *   vector_map = {
 *     <attribute>: {                       // e.g. "passing"
 *       <code>: {                          // sub-attribute, e.g. "SP"
 *         <eventId>: {                      // e.g. "0"
 *           alias,
 *           vector_map: [ { user_id, user_name, jersey_no, starting_point,
 *                           ending_point, starting_video, footing,
 *                           special_event, ... } ]
 *         }
 *       }
 *     }
 *   }
 *
 * Output matches the tree the UI already renders:
 *   [{ label, children: [{ label, children: [{ label, count, events }] }] }]
 * with each event = { jersey, name, time, str } (str is a synthesised 12-field
 * token so the existing EventDetails / field-map keep working).
 */

import { attribTitle, ATTRIBUTE_LABEL } from "../common/abbr";

const prettify = (type) =>
  String(type || "")
    .split("_")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");

// match_video → game clock "MM:SS" (minutes uncapped, e.g. 66:30, 90:00).
// Accepts seconds (number/string) or "HH:MM:SS" / "MM:SS".
const toGameTime = (val) => {
  if (val == null || val === "") return "";
  let total;
  if (typeof val === "string" && val.includes(":")) {
    const parts = val.split(":").map((n) => parseInt(n, 10) || 0);
    // h:m:s → h*3600 + m*60 + s ; m:s → m*60 + s
    total =
      parts.length === 3
        ? parts[0] * 3600 + parts[1] * 60 + parts[2]
        : parts[0] * 60 + parts[1];
  } else {
    total = Math.round(Number(val));
  }
  if (!Number.isFinite(total)) return "";
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const synthStr = (team, jersey, code, eventId, v) =>
  [
    team || "X",
    jersey || "X",
    code,
    eventId,
    v.starting_point ?? "X",
    v.ending_point ?? "X",
    v.starting_video || "X",
    v.footing ?? "X",
    v.special_event ?? "X",
    "X",
    "X",
    "X",
  ].join("-");

/**
 * @param {object|object[]} vectorMaps  one vector_map, or several (e.g. team A
 *   and team B) which are merged into a single tree
 * @param {object} [maps]
 * @param {Record<number,string>} [maps.teamByUserId]  user_id → "A"|"B"
 * @param {Record<number,string>} [maps.nameByUserId]  user_id → display name
 */
export function parseSenseVectorTree(
  vectorMaps,
  { teamByUserId = {}, nameByUserId = {}, jerseyByUserId = {}, matchString = "" } = {}
) {
  const list = (Array.isArray(vectorMaps) ? vectorMaps : [vectorMaps]).filter(Boolean);
  // attrLabel → Map(subLabel → Map(eventLabel → events[]))
  const attrMap = new Map();

  // Index the real event tokens from the match_string so each vector can reuse
  // the actual string instead of a reconstructed one. Match on the fields shared
  // by both, in order: Team | Action(code) | Jersey | Start | End | Time.
  //   token: Team-Jersey-Code-EventId-Start-End-Time-...
  //   vector: team, code, jersey_no, starting_point, ending_point, starting_video
  const tokenKey = (team, code, jersey, start, end, time) =>
    `${team}|${code}|${jersey}|${start}|${end}|${time}`;
  const tokenIndex = new Map();
  String(matchString || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((tok) => {
      const f = tok.split("-");
      if (f.length < 7) return;
      const key = tokenKey(f[0], f[2], f[1], f[4], f[5], f[6]);
      if (!tokenIndex.has(key)) tokenIndex.set(key, tok);
    });

  list.forEach((vm) => {
    Object.keys(vm || {}).forEach((attribute) => {
      const subs = vm[attribute] || {};
      Object.keys(subs).forEach((code) => {
        const meta = attribTitle[code];
        const events = subs[code] || {};
        const attrLabel = ATTRIBUTE_LABEL[meta?.type || attribute] || prettify(attribute);
        const subLabel = meta?.name || code;

        Object.keys(events).forEach((eventId) => {
          const actData = events[eventId] || {};
          const vectors = actData.vector_map || [];
          if (!vectors.length) return;
          const eventLabel = meta?.events?.[eventId] ?? actData.alias ?? `Event ${eventId}`;

          if (!attrMap.has(attrLabel)) attrMap.set(attrLabel, new Map());
          const subMap = attrMap.get(attrLabel);
          if (!subMap.has(subLabel)) subMap.set(subLabel, new Map());
          const evMap = subMap.get(subLabel);
          if (!evMap.has(eventLabel)) evMap.set(eventLabel, []);

          vectors.forEach((v) => {
            const uid = v.user_id;
            const team = teamByUserId[uid] || v.team_notation || "";
            // Fall back to the players list (by user_id) when the vector has no jersey.
            const jersey = v.jersey_no ?? jerseyByUserId[uid] ?? "";
            const time = v.starting_video || "";
            const start = v.starting_point ?? "X";
            const end = v.ending_point ?? "X";
            // Prefer the real token from the match string; fall back to synth.
            // Match by Team | Action | Jersey | Start | End | Time.
            const realStr = tokenIndex.get(
              tokenKey(team, code, String(jersey), String(start), String(end), time)
            );
            evMap.get(eventLabel).push({
              jersey: jersey === "X" ? "" : jersey,
              name: nameByUserId[uid] || v.user_name || "",
              time,
              matchVideo: toGameTime(v.match_video),
              str: realStr || synthStr(team, jersey, code, eventId, v),
              data: v,
            });
          });
        });
      });
    });
  });

  return [...attrMap.entries()].map(([attrLabel, subMap]) => ({
    label: attrLabel,
    children: [...subMap.entries()].map(([subLabel, evMap]) => ({
      label: subLabel,
      children: [...evMap.entries()].map(([eventLabel, events]) => ({
        label: eventLabel,
        count: events.length,
        events,
      })),
    })),
  }));
}
